<template>
  <div class="app-layout" :class="{ 'app-layout--sidebar-open': sidebarOpen }">
    <div
      class="app-sidebar-backdrop"
      aria-hidden="true"
      @click="sidebarOpen = false"
    ></div>
    <aside class="app-sidebar ui-glass-panel" aria-label="Main navigation">
      <div class="sidebar-brand">
        <router-link
          class="sidebar-brand__link"
          to="/home"
          @click.native="closeSidebar"
        >
          <span class="sidebar-brand__title">AKKURATE POS</span>
          <span class="sidebar-brand__sub">Retail</span>
        </router-link>
      </div>

      <nav class="sidebar-nav">
        <router-link
          class="sidebar-link"
          to="/home"
          @click.native="closeSidebar"
        >
          <font-awesome-icon
            class="me-2 .quick-link-icon"
            size="sm"
            :icon="['fas', 'dashboard']"
          />
          Dashboard
        </router-link>

        <template v-if="canSell">
          <div
            class="sidebar-heading"
            :class="{ 'is-open': openSection === 'sales' }"
            @click="toggleSection('sales')"
          >
            <span>Sales</span>
            <svg
              class="sidebar-heading__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="sidebar-submenu" v-show="openSection === 'sales'">
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/pos"
              @click.native="closeSidebar"
              ><font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'cash-register']"
              />
              POS</router-link
            >
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/sales"
              @click.native="closeSidebar"
              ><font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'file-invoice-dollar']"
              />
              Sales</router-link
            >
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/cash-drawer"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'vault']"
              />
              Cash drawer
            </router-link>
          </div>
        </template>

        <template v-if="canManageProducts">
          <div
            class="sidebar-heading"
            :class="{ 'is-open': openSection === 'catalog' }"
            @click="toggleSection('catalog')"
          >
            <span>Catalog</span>
            <svg
              class="sidebar-heading__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="sidebar-submenu" v-show="openSection === 'catalog'">
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/customers"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'users']"
              />
              Customers
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/products"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'tag']"
              />
              Products
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/suppliers"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'truck']"
              />
              Suppliers
            </router-link>
          </div>
        </template>

        <template v-if="canViewInventory">
          <div
            class="sidebar-heading"
            :class="{ 'is-open': openSection === 'inventory' }"
            @click="toggleSection('inventory')"
          >
            <span>Inventory</span>
            <svg
              class="sidebar-heading__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="sidebar-submenu" v-show="openSection === 'inventory'">
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/stock-receipts"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'file-invoice']"
              />
              Stock receipts
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/purchase-orders"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'cart-shopping']"
              />

              Purchase orders
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/inventory"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'boxes']"
              />
              Inventory
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/transfers"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'exchange-alt']"
              />
              Transfers
            </router-link>
          </div>
        </template>

        <template v-if="canViewReports || canViewAccounting">
          <div
            class="sidebar-heading"
            :class="{ 'is-open': openSection === 'finance' }"
            @click="toggleSection('finance')"
          >
            <span>Finance</span>
            <svg
              class="sidebar-heading__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="sidebar-submenu" v-show="openSection === 'finance'">
            <router-link
              v-if="canViewReports"
              class="sidebar-link sidebar-link--sub"
              to="/reports"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'chart-line']"
              />
              Reports
            </router-link>
            <router-link
              v-if="canViewAccounting"
              class="sidebar-link sidebar-link--sub"
              to="/accounting"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'file-invoice-dollar']"
              />
              Accounting
            </router-link>
          </div>
        </template>

        <template v-if="canManageUsers">
          <div
            class="sidebar-heading"
            :class="{ 'is-open': openSection === 'admin' }"
            @click="toggleSection('admin')"
          >
            <span>Admin</span>
            <svg
              class="sidebar-heading__chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="sidebar-submenu" v-show="openSection === 'admin'">
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/users"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'users']"
              />
              Users</router-link
            >
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/promotions"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'tags']"
              />
              Promotions
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/quick-keys"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'keyboard']"
              />
              Quick keys
            </router-link>
            <router-link
              class="sidebar-link sidebar-link--sub"
              to="/settings"
              @click.native="closeSidebar"
            >
              <font-awesome-icon
                class="me-2 .quick-link-icon"
                size="sm"
                :icon="['fas', 'cogs']"
              />
              Settings
            </router-link>
          </div>
        </template>
      </nav>

      <div class="sidebar-footer">
        <b-form-select
          v-if="userStores.length > 1"
          v-model="localStoreId"
          size="sm"
          class="store-select w-100 mb-2 form-select"
          :options="storeOptions"
        />
        <div
          v-else-if="selectedStore"
          class="sidebar-store small text-muted mb-2"
        >
          {{ selectedStore.storeName }}
        </div>
        <div v-if="user" class="sidebar-user small text-muted mb-2">
          {{ user.firstName }} {{ user.lastName }}
        </div>
        <icon-button
          v-if="user"
          :func="() => logout()"
          :icon="'door-open'"
          :title="'Logout'"
          :classes="'akkurate-green-bg btn-size2 w-100'"
          :btnName="'Logout'"
        />
      </div>
    </aside>

    <div class="app-main">
      <header class="app-topbar ui-glass-panel">
        <button
          type="button"
          class="sidebar-toggle btn btn-sm btn-outline-secondary"
          aria-label="Toggle navigation menu"
          :aria-expanded="sidebarOpen ? 'true' : 'false'"
          @click="sidebarOpen = !sidebarOpen"
        >
          <span class="sidebar-toggle__bar" />
          <span class="sidebar-toggle__bar" />
          <span class="sidebar-toggle__bar" />
        </button>
        <span class="app-topbar__title text-muted small">{{ pageTitle }}</span>
      </header>

      <main class="app-canvas">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import IconButton from "@/components/IconButton.vue";

const PAGE_TITLES = {
  home: "Dashboard",
  pos: "POS",
  sales: "Sales",
  "cash-drawer": "Cash drawer",
  customers: "Customers",
  products: "Products",
  suppliers: "Suppliers",
  "stock-receipts": "Stock receipts",
  "purchase-orders": "Purchase orders",
  inventory: "Inventory",
  transfers: "Transfers",
  reports: "Reports",
  accounting: "Accounting",
  users: "Users",
  promotions: "Promotions",
  "quick-keys": "Quick keys",
  settings: "Settings",
};

export default {
  name: "MainLayout",
  data() {
    return {
      sidebarOpen: false,
      openSection: null,
    };
  },
  components: {
    IconButton,
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "hasPermission",
      "selectedStoreId",
      "selectedStore",
      "userStores",
    ]),
    user() {
      return this.currentUser;
    },
    storeOptions() {
      return this.userStores.map((s) => ({
        value: s.storeId,
        text: s.storeName,
      }));
    },
    pageTitle() {
      return PAGE_TITLES[this.$route.name] || "AKKURATE POS";
    },
    canSell() {
      const hasP = this.hasPermission("SALE");

      if (hasP && this.openSection == null) this.openSection = "sales";
      return hasP;
    },
    canManageProducts() {
      const hasP = this.hasPermission("INVENTORY_VIEW");

      if (hasP && this.openSection == null) this.openSection = "catalog";
      return hasP;
    },
    canViewInventory() {
      const hasP = this.hasPermission("INVENTORY_VIEW");

      if (hasP && this.openSection == null) this.openSection = "inventory";
      return hasP;
    },
    canViewReports() {
      const hasP = this.hasPermission("REPORTS_VIEW");

      if (hasP && this.openSection == null) this.openSection = "finance";
      return hasP;
    },
    canViewAccounting() {
      const hasP = this.hasPermission("ACCOUNTING_VIEW");

      if (hasP && this.openSection == null) this.openSection = "finance";
      return hasP;
    },
    canManageUsers() {
      const hasP = this.hasPermission("USER_MANAGE");

      if (hasP && this.openSection == null) this.openSection = "admin";
      return hasP;
    },
    localStoreId: {
      get() {
        return this.selectedStoreId;
      },
      set(storeId) {
        this.$store.commit("SET_STORE", storeId);
      },
    },
  },
  watch: {
    $route() {
      this.sidebarOpen = false;
    },
  },
  mounted() {
    this.$store.dispatch("loadSettings");
  },
  methods: {
    closeSidebar() {
      this.sidebarOpen = false;
    },
    logout() {
      this.$store.commit("LOGOUT");
      this.$router.push({ name: "login" });
    },
    toggleSection(key) {
      this.openSection = this.openSection === key ? null : key;
    },
  },
};
</script>
